import { useState, useEffect, useCallback } from 'react';
import { FRONTEND_VERSION } from '../version';

const useFrontendVersionCheck = () => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);

  // Función para obtener la versión actual del frontend (desde el navegador)
  const getCurrentFrontendVersion = () => {
    // Usar la versión del archivo version.js
    return FRONTEND_VERSION || localStorage.getItem('frontendVersion') || 'unknown';
  };

  // Función para obtener la versión del servidor (archivos estáticos)
  const getServerFrontendVersion = useCallback(async () => {
    // En desarrollo, no intentar obtener versión del servidor ya que los archivos no están servidos estáticamente
    if (process.env.NODE_ENV === 'development') {
      return null;
    }

    try {
      // Método 1: Intentar obtener versión desde un endpoint específico del servidor
      try {
        const versionResponse = await fetch(`/version.json?t=${Date.now()}`, {
          method: 'GET',
          cache: 'no-cache',
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (versionResponse.ok) {
          const versionData = await versionResponse.json();
          // ✅ Usar timestamp para comparación consistente
          return versionData.timestamp || versionData.version || versionData.buildDate || 'unknown';
        }
      } catch (versionError) {
        // Si no existe el endpoint de versión, continuar con otros métodos
        // No mostrar error en consola para evitar spam
      }

      // Método 2: Usar el archivo principal de JS con cache-busting
      try {
        const response = await fetch(`/static/js/main.js?t=${Date.now()}`, {
          method: 'HEAD',
          cache: 'no-cache'
        });

        if (response.ok) {
          // Obtener la fecha de última modificación del archivo
          const lastModified = response.headers.get('last-modified');
          const etag = response.headers.get('etag');
          
          // Convertir fecha a timestamp para comparación consistente
          if (lastModified) {
            const timestamp = new Date(lastModified).toISOString();
            return timestamp;
          }
          
          // Si no hay lastModified, usar etag pero limpiado
          if (etag) {
            // Limpiar etag de comillas y usar como identificador
            const cleanEtag = etag.replace(/"/g, '').replace(/^W\//, '');
            return cleanEtag;
          }
        }
      } catch (jsError) {
        // Continuar con otros métodos si este falla
      }

      // Método 3: Intentar con el HTML principal
      try {
        const htmlResponse = await fetch(`/?t=${Date.now()}`, {
          method: 'HEAD',
          cache: 'no-cache'
        });
        
        if (htmlResponse.ok) {
          const lastModified = htmlResponse.headers.get('last-modified');
          if (lastModified) {
            return new Date(lastModified).toISOString();
          }
        }
      } catch (htmlError) {
        // Si todos los métodos fallan, retornar null silenciosamente
      }
      
      return null;
      // eslint-disable-next-line no-unreachable
    } catch (error) {
      // Silenciar errores - es normal que falle en algunos entornos
      return null;
    }
  }, []);

  // Función para normalizar versiones para comparación

  // Función para normalizar versiones para comparación
  const normalizeVersion = (version) => {
    if (!version) return null;
    
    // Si es un timestamp ISO, usarlo directamente
    if (version.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return version;
    }
    
    // Si es una fecha, convertirla a ISO
    try {
      const date = new Date(version);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    } catch (e) {
      // Continuar con otros métodos
    }
    
    // Si es un hash o etag, limpiarlo y usar como está
    return String(version).replace(/"/g, '').replace(/^W\//, '').trim();
  };

  // Función para verificar si hay una nueva versión disponible
  const checkForUpdates = useCallback(async () => {
    try {
      setIsChecking(true);
      
      const currentVersion = getCurrentFrontendVersion();
      const serverVersion = await getServerFrontendVersion();
      
      if (serverVersion && currentVersion) {
        // Normalizar ambas versiones para comparación
        const normalizedCurrent = normalizeVersion(currentVersion);
        const normalizedServer = normalizeVersion(serverVersion);
        
        // Solo comparar si ambas versiones son válidas
        const hasNewVersion = normalizedCurrent && normalizedServer && 
                             normalizedCurrent !== normalizedServer;
        
        setVersionInfo({
          current: normalizedCurrent || currentVersion,
          server: normalizedServer || serverVersion,
          hasNewVersion
        });
        
        // Solo mostrar notificación si hay una nueva versión y no la hemos ignorado
        // ✅ Comparar por fecha/timestamp para asegurar que la nueva versión es realmente más nueva
        const ignoredVersion = localStorage.getItem('ignoredVersion');
        const shouldShowNotification = hasNewVersion && 
                                      ignoredVersion !== normalizedServer &&
                                      normalizedCurrent !== normalizedServer &&
                                      new Date(normalizedServer) > new Date(normalizedCurrent || '2000-01-01');
        
        if (shouldShowNotification) {
          setNewVersionAvailable(true);
          setShowUpdatePrompt(true);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 Nueva versión del frontend disponible');
            console.log('📱 Versión actual (navegador):', normalizedCurrent || currentVersion);
            console.log('🌐 Versión del servidor:', normalizedServer || serverVersion);
            console.log('🔍 Comparación normalizada:', {
              current: normalizedCurrent,
              server: normalizedServer,
              hasNewVersion
            });
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ Frontend actualizado');
            if (ignoredVersion === normalizedServer) {
              console.log('⏭️ Actualización ignorada para esta versión');
            }
          }
        }
      } else {
        // Silenciar advertencia - es normal en desarrollo o si no hay servidor disponible
        // Solo mostrar en desarrollo si realmente necesitamos debugging
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_VERSION === 'true') {
          console.log('⚠️ No se pudo obtener versión del servidor o navegador');
        }
      }
    } catch (error) {
      // Silenciar errores - es normal que falle en desarrollo o en algunos entornos
      // Solo mostrar en desarrollo si realmente necesitamos debugging
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_VERSION === 'true') {
        console.error('❌ Error verificando actualizaciones:', error);
      }
    } finally {
      setIsChecking(false);
    }
  }, [getServerFrontendVersion]);

  // Función para limpiar cache y recargar
  const updateToNewVersion = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Actualizando a nueva versión...');
    }
    
    // Limpiar todo el cache del navegador
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Limpiar localStorage de versiones
    localStorage.removeItem('ignoredVersion');
    
    // Limpiar sessionStorage
    sessionStorage.clear();
    
    // Recargar la página forzando la descarga de archivos frescos
    window.location.reload(true);
  };

  // Función para ignorar la actualización por esta sesión
  const ignoreUpdate = () => {
    if (versionInfo?.server) {
      const normalizedServer = normalizeVersion(versionInfo.server);
      localStorage.setItem('ignoredVersion', normalizedServer || versionInfo.server);
    }
    setShowUpdatePrompt(false);
    setNewVersionAvailable(false);
    if (process.env.NODE_ENV === 'development') {
      console.log('⏭️ Actualización ignorada para esta versión');
    }
  };

  // Verificar actualizaciones al cargar la aplicación
  useEffect(() => {
    // Esperar un poco para que la aplicación se cargue completamente
    const timer = setTimeout(() => {
      checkForUpdates();
    }, 2000);

    return () => clearTimeout(timer);
  }, [checkForUpdates]);

  // Verificar actualizaciones cuando la ventana recupera el foco
  useEffect(() => {
    const handleFocus = () => {
      // Verificar actualizaciones cuando el usuario vuelve a la pestaña
      checkForUpdates();
    };

    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkForUpdates]);

  return {
    newVersionAvailable,
    isChecking,
    versionInfo,
    showUpdatePrompt,
    checkForUpdates,
    updateToNewVersion,
    ignoreUpdate,
    setShowUpdatePrompt
  };
};

export default useFrontendVersionCheck;
